import { Fragment, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useCookies } from "react-cookie";
import { LoginService } from "./services/login.service";
import './styles/login.styles.scss';
import { ILoginData, ILoginDispatch, ILoginReponse, IReduxDispatchable, IUserLookup, KeyType } from "./interfaces/login.interface";
import { AjaxError } from "rxjs/ajax";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faUser, faKey } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch } from "app/hooks";
import { login } from "lib/reducers/user.slice";
import { concatMap } from "rxjs";
import { LoaderService } from "lib/services/loader.service";


export default function Login() {
    const [cookies, setCookie, removeCookie] = useCookies();
    const usernameFromCookie = cookies.QuestUsername; // Check for username in the cookie "QuestUsername"
    const params = useParams(); // Check for a username in the URL
    const dispatch = useAppDispatch();

    const usernameFromUrl = params.username; // Check for a username in the URL
    const [encryptedPqp, setEncryptedPqp] = useState(new URLSearchParams(window.location.search));

    const [firstName, setFirstName]: [string, Function] = useState("");
    const [message, setMessage]: [string, Function] = useState("");

    const [showPassword, setShowPassword]: [boolean, Function] = useState(false);
    const [loading, setLoading]: [boolean, Function] = useState(false);
    const loginService: LoginService = new LoginService();
    const [loginData, setLoginData]: [ILoginData, Function] = useState({
        username: "",
        password: ""
    });
    const [rememberMe, setRememberMe]: [boolean, Function] = useState(false);
    const [dispatchable, setDispatchable]: [IReduxDispatchable<ILoginDispatch>, Function] = useState({} as IReduxDispatchable<ILoginDispatch>);
    const loaderService = new LoaderService();
    let userLoginData: any = {};

    const doLogin = (event: any) => {
        setLoading(true);
        event.preventDefault();
        loginService.Login(loginData.username, loginData.password)
            .pipe(
                concatMap(obs => {
                    userLoginData = { ...obs.response };
                    return loginService.GetUserProfile(obs.response.user.UserID, obs.response.user.UserType)
                })
            )
            .subscribe({
                next: (response) => {
                    const loginData: ILoginReponse = {
                        ...response.response,
                        ...userLoginData
                    }
                    processSuccess(loginData);
                },
                error: (response) => {
                    console.log('error response', response);
                    processFailure(response);
                }
            })
    }

    const redirectToReset = (response: ILoginReponse) => {
        window.location.href = `/change-password/${response.reset_token}`;
    }

    const processSuccess = (response: ILoginReponse) => {
        if (rememberMe) {
            setCookie("QuestUsername", loginData.username, { path: "/", });
        }

        if (response.resetPassword) {
            redirectToReset(response);
            return;
        }

        setDispatchable({ ...loginService.DispatchLogin(response) })
    }

    const processFailure = (error: AjaxError) => {
        setLoginData((previousState: any) => {
            return { ...previousState, Password: "" };
        });
        setMessage(loginService.BuildMessage(error));
        setLoading(false);
    }

    const handleUsernameChange = (input: any) => {
        const value = input.target.value;
        setLoginData((previousState: any) => {
            return { ...previousState, username: value };
        });
    }

    const handlePasswordChange = (input: any) => {
        const value = input.target.value;
        setLoginData((previousState: any) => {
            return { ...previousState, password: value };
        });
    }

    const handleNotYou = () => {
        let route = window.location.href.split('/');
        removeCookie("QuestUsername");
        if (route) {
            route.pop();
            window.location.href = route.join('/');
        }
    }

    const handleRememberMeCheckBox = () => {
        setRememberMe(!rememberMe);
    }

    useEffect(() => {
        loaderService.hide();
        if (usernameFromUrl || usernameFromCookie) {
            loginService.GetUserFromUsername(usernameFromUrl ? usernameFromUrl : usernameFromCookie)
                .subscribe({
                    next: ({ response }: { response: IUserLookup }) => {
                        setFirstName(response.FirstName);
                        setLoginData((previousState: any) => {
                            return { ...previousState, username: response.Username };
                        });
                    }
                })
        }


    }, [usernameFromCookie, usernameFromUrl]);

    useEffect(() => {
      loaderService.hide();
      if(!encryptedPqp || !encryptedPqp.get('pqp')) return;
      loginService.GetUserFromEncryptedPqp(encryptedPqp.get('pqp') ?? "")
        .pipe(
          concatMap(obs => {
            userLoginData = { ...obs.response };
            return loginService.GetUserProfile(obs.response.user.UserID, obs.response.user.UserType)
          })
        )
        .subscribe({
          next: (response) => {
            const loginData: ILoginReponse = {
              ...response.response,
              ...userLoginData
            }
            processSuccess(loginData);
          },
          error: (response) => {
            processFailure(response);
          }
        })
    }, [encryptedPqp])

    useEffect(() => {
        if (dispatchable.type === 'user/logIn')
            dispatch(login({ ...dispatchable.payload }));
    }, [dispatchable])

    return (
        <Fragment>
            <h4>
                <div>
                    Welcome
                    {(firstName !== "")
                        ? ` back to Quest, ${firstName}`
                        : ` to Quest`
                    }
                </div>
                <span>Please sign in to your account</span>
            </h4>
            {message !== "" && <div
                className="alert alert-danger mx-auto message-center"
            >
                <span>{message !== "" ? message : ""}</span>
            </div>}

            <div id="login-form" className="mx-auto" onKeyPress={(target) => { loginService.DetermineKeyPressed(target.key, KeyType.Enter) && doLogin(target) }}>
                <form onSubmit={(event) => doLogin(event)}>
                    <div className="row">
                        <div className="col">
                            <div className="form-group row no-gutters">
                                <div className="col">
                                    <div className="d-flex justify-content-between">
                                        <label htmlFor="username-input" className="form-label mb-0">Username</label>
                                        {firstName !== "" &&
                                            <span className="ml-auto text-small pointer text-link" onClick={(event) => handleNotYou()}>Not You?</span>
                                        }
                                    </div>
                                    <div className="row">
                                        <div className="input-group">
                                            <div className="input-group-prepend">
                                                <span className="input-group-text">
                                                    <FontAwesomeIcon icon={faUser} />
                                                </span>
                                            </div>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Username here..."
                                                value={loginData.username}
                                                onChange={handleUsernameChange}
                                                id="username-input"
                                                required={true}
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>
                                </div>


                            </div>
                        </div>
                    </div>

                    <div className="row mt-3">
                        <div className="col">
                            <div className="form-group row no-gutters">
                                <div className="col">
                                    <div className="d-flex justify-content-between">
                                        <label htmlFor="password-input" className="form-label mb-0">Password</label>
                                        <Link
                                            to={"/recover-password" + ((loginData.username !== "") ? "/" + loginData.username : "")}
                                            className="ml-auto text-link text-small">
                                            Forgot Password or Username
                                        </Link>
                                    </div>
                                    <div className="row">
                                        <div className="input-group">
                                            <div className="input-group-prepend">
                                                <span className="input-group-text">
                                                    <FontAwesomeIcon icon={faKey} />
                                                </span>
                                            </div>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                className="form-control"
                                                value={loginData.password}
                                                placeholder="Password here..."
                                                onChange={handlePasswordChange}
                                                id="password-input"
                                                required={true}
                                                disabled={loading}
                                            />
                                            <div className="input-group-append">
                                                <span className="input-group-text" onClick={() => setShowPassword(!showPassword)}>
                                                    <FontAwesomeIcon icon={!showPassword ? faEye : faEyeSlash} className="pointer" />
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                            </div>
                        </div>
                    </div>

                    <div className="justify-content-center row mt-2 mb-3">
                        <div className="form-group">
                            <input
                                type="checkbox"
                                name="rememberMe"
                                id="rememberMe"
                                checked={rememberMe}
                                onChange={handleRememberMeCheckBox}
                                disabled={loading}
                            />
                            <label htmlFor="rememberMe" className="ml-1 mb-0"> Remember Me</label>
                        </div>
                    </div>

                    <div className="justify-content-center row">
                        <button className="btn btn-primary" type="submit" disabled={loading}>
                            <span className={`${!loading ? '' : 'hidden'}`}>{loading ? "Signing In ..." : "Sign in to Quest"}</span>
                            <span className={`spinner-border spinner-border-sm ${loading ? '' : 'hidden'}`}></span>
                        </button>
                    </div>
                </form>
            </div>
        </Fragment>
    )
}
